'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetch } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import { clawsListSessions } from '../apis';
import { PAGE_SIZE } from '../constants';

const LOAD_MORE_THRESHOLD = 120;

/**
 * Nav 会话列表的分页与懒加载。
 * - 随 activeClaw 切换自动重置并拉第一页
 * - containerRef 上的 scroll 监听 + 数据变化复查触底，跨浏览器一致
 * - sessions 本身仍由 store 统一管理（其它模块依赖）
 */
export const useClawSessions = () => {
  const storeApi = useStoreApi();
  const { activeClaw, sessions, sessionsOwnerClaw, setSessions, clearSessions } = useStore((state) => ({
    activeClaw: state.activeClaw,
    sessions: state.sessions,
    sessionsOwnerClaw: state.sessionsOwnerClaw,
    setSessions: state.setSessions,
    clearSessions: state.clearSessions,
  }));

  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const nextPageRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const { runAsync: fetchSessionsAsync } = useFetch(
    ({ clawId, page }: { clawId: string; page: number }) =>
      clawsListSessions({
        query: {
          'pager.page': page,
          'pager.pagesz': PAGE_SIZE,
          claw_id: clawId,
        },
      }),
    { manual: true },
  );

  const loadPage = useCallback(
    async ({ clawId, page }: { clawId: string; page: number }) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setIsLoading(true);
      const replace = page === 0;

      try {
        const response = await fetchSessionsAsync({ clawId, page });

        const latestActiveClaw = storeApi.getState().activeClaw;
        if (latestActiveClaw?.id !== clawId) return;

        if (response.error || !response.data) {
          if (replace) setSessions(latestActiveClaw, []);
          hasMoreRef.current = false;
          setHasMore(false);
          return;
        }

        const pageSessions = response.data.sessions ?? [];
        const base = replace ? [] : storeApi.getState().sessions;
        const merged = Array.from(new Map([...base, ...pageSessions].map((s) => [s.id, s])).values());

        setSessions(latestActiveClaw, merged);

        const total = response.data.total;
        const reachedEnd = pageSessions.length < PAGE_SIZE || (typeof total === 'number' && merged.length >= total);
        hasMoreRef.current = !reachedEnd;
        setHasMore(!reachedEnd);
        nextPageRef.current = page + 1;
      } finally {
        loadingRef.current = false;
        setIsLoading(false);
      }
    },
    [fetchSessionsAsync, setSessions, storeApi],
  );

  useEffect(() => {
    const resetPaging = () => {
      hasMoreRef.current = true;
      setHasMore(true);
      nextPageRef.current = 0;
      loadingRef.current = false;
    };

    if (!activeClaw?.id) {
      clearSessions();
      resetPaging();
      return;
    }

    if (sessionsOwnerClaw?.id === activeClaw.id) return;

    resetPaging();
    loadPage({ clawId: activeClaw.id, page: 0 });
  }, [activeClaw?.id, sessionsOwnerClaw?.id, clearSessions, loadPage]);

  // 触底加载：scroll 触发 + sessions 长度变化时复查（覆盖首屏数据没铺满容器的情况）。
  const activeClawId = activeClaw?.id;
  useEffect(() => {
    const root = containerRef.current;
    if (!root || !activeClawId) return;

    const checkAndLoad = () => {
      if (loadingRef.current || !hasMoreRef.current) return;
      if (root.scrollHeight - root.scrollTop - root.clientHeight > LOAD_MORE_THRESHOLD) return;
      loadPage({ clawId: activeClawId, page: nextPageRef.current });
    };

    checkAndLoad();
    root.addEventListener('scroll', checkAndLoad, { passive: true });
    return () => root.removeEventListener('scroll', checkAndLoad);
  }, [activeClawId, sessions.length, loadPage]);

  return {
    sessions,
    isLoading,
    isInitialLoading: isLoading && sessions.length === 0,
    hasMore,
    containerRef,
  };
};
